import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Edit03, Mail01, Phone01, Plus, Ruler, SearchLg, XClose } from '@untitledui/icons';
import { toast } from 'react-toastify';
import { AuthContext } from '../App';
import { memberService, MemberResponse, MembersListParams } from '../services/memberService';
import { Avatar } from './untitled-ui/base/avatar/avatar';
import { Button } from './untitled-ui/base/buttons/button';
import { Input } from './untitled-ui/base/input/input';

const initials = (name: string) => name.split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase();
const titleCase = (value?: string) => (value || 'N/A').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
const formatDate = (value?: string) => value ? new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const Members: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [members, setMembers] = useState<MemberResponse[]>([]);
  const [selectedMember, setSelectedMember] = useState<MemberResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMembership, setFilterMembership] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchMembers = async () => {
      setIsLoading(true);
      try {
        const params: MembersListParams = { page: currentPage, size: pageSize, search: searchTerm, status: filterStatus, membershipType: filterMembership };
        const response = await memberService.getMembers(params);
        if (!response.success || !response.data) throw new Error('Unable to load members');
        setMembers(response.data.members);
        setTotalPages(response.data.totalPages);
        setTotalElements(response.data.totalElements);
      } catch (error: any) {
        toast.error(error?.message || 'Failed to load members');
      } finally {
        setIsLoading(false);
      }
    };
    const timeout = window.setTimeout(fetchMembers, searchTerm ? 250 : 0);
    return () => window.clearTimeout(timeout);
  }, [currentPage, searchTerm, filterStatus, filterMembership, isAuthenticated]);

  const updateStatus = async (member: MemberResponse) => {
    const nextStatus = member.status.toUpperCase() === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      await memberService.updateMemberStatus(member.id, nextStatus);
      setMembers(items => items.map(item => item.id === member.id ? { ...item, status: nextStatus } : item));
      setSelectedMember(current => current?.id === member.id ? { ...current, status: nextStatus } : current);
      toast.success(`${member.fullName} is now ${nextStatus.toLowerCase()}`);
    } catch {
      toast.error('Failed to update member status');
    }
  };

  return (
    <div className="u-page members-page">
      <div className="u-page-header">
        <div>
          <h2>Member directory</h2>
          <p>Manage profiles, memberships, health data, and workout plans.</p>
        </div>
        <Button size="md" color="primary" iconLeading={Plus} onPress={() => navigate('/register')}>Add member</Button>
      </div>

      <div className="u-tabs" role="tablist">
        <button className="active" type="button">All members <span>{totalElements}</span></button>
        <button type="button" onClick={() => setFilterStatus('ACTIVE')}>Active</button>
        <button type="button" onClick={() => setFilterStatus('SUSPENDED')}>Suspended</button>
      </div>

      <section className="u-panel">
        <div className="u-toolbar">
          <Input className="u-search" icon={SearchLg} placeholder="Search name, member number, email or NIC..." value={searchTerm} onChange={value => { setSearchTerm(value); setCurrentPage(0); }} />
          <div className="u-toolbar-filters">
            <select className="u-select" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(0); }} aria-label="Member status">
              <option value="">All statuses</option><option value="ACTIVE">Active</option><option value="SUSPENDED">Suspended</option><option value="EXPIRED">Expired</option>
            </select>
            <select className="u-select" value={filterMembership} onChange={e => { setFilterMembership(e.target.value); setCurrentPage(0); }} aria-label="Membership type">
              <option value="">All memberships</option><option value="DAILY">Daily</option><option value="WEEKLY">Weekly</option><option value="MONTHLY">Monthly</option><option value="QUARTERLY">Quarterly</option><option value="BIANNUAL">Biannual</option><option value="ANNUAL">Annual</option>
            </select>
          </div>
        </div>

        <div className="u-table-wrap">
          <table className="u-table">
            <thead><tr><th>Member</th><th>Membership</th><th>Status</th><th>Phone</th><th>Start date</th><th aria-label="Actions"></th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={6}><div className="u-state"><span className="spinner" />Loading members…</div></td></tr>
              ) : members.length === 0 ? (
                <tr><td colSpan={6}><div className="u-state"><strong>No members found</strong><span>Try changing your filters or add a new member.</span></div></td></tr>
              ) : members.map(member => (
                <tr key={member.id} onClick={() => setSelectedMember(member)}>
                  <td><div className="u-person"><Avatar size="sm" src={member.profilePhoto} alt={member.fullName} initials={initials(member.fullName)} /><span><strong>{member.fullName}</strong><small>#{member.memberNumber} · {member.email || 'No email'}</small></span></div></td>
                  <td><strong className="u-cell-primary">{titleCase(member.membershipType)}</strong></td>
                  <td><span className={`u-badge u-badge-${member.status.toLowerCase()}`}><i />{titleCase(member.status)}</span></td>
                  <td>{member.phone || '—'}</td>
                  <td>{formatDate(member.startDate)}</td>
                  <td><button type="button" className="u-row-action" onClick={e => { e.stopPropagation(); setSelectedMember(member); }} aria-label={`Open ${member.fullName}`}><ChevronRight /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="u-pagination">
          <span>Showing {members.length ? currentPage * pageSize + 1 : 0}–{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements}</span>
          <div>
            <Button size="sm" color="secondary" iconLeading={ChevronLeft} isDisabled={currentPage === 0} onPress={() => setCurrentPage(page => page - 1)}>Previous</Button>
            <span className="u-page-number">{currentPage + 1}</span>
            <Button size="sm" color="secondary" iconTrailing={ChevronRight} isDisabled={currentPage >= totalPages - 1} onPress={() => setCurrentPage(page => page + 1)}>Next</Button>
          </div>
        </div>
      </section>

      {selectedMember && (
        <div className="u-drawer-layer" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setSelectedMember(null); }}>
          <aside className="u-drawer" role="dialog" aria-modal="true" aria-label="Member details">
            <div className="u-drawer-header">
              <div><h2>Member details</h2><p>Profile and membership information</p></div>
              <Button size="sm" color="tertiary" iconLeading={XClose} aria-label="Close" onPress={() => setSelectedMember(null)} />
            </div>
            <div className="u-profile-summary">
              <Avatar size="xl" src={selectedMember.profilePhoto} alt={selectedMember.fullName} initials={initials(selectedMember.fullName)} status={selectedMember.status.toUpperCase() === 'ACTIVE' ? 'online' : 'offline'} />
              <div><h3>{selectedMember.fullName}</h3><p>#{selectedMember.memberNumber}</p><span className={`u-badge u-badge-${selectedMember.status.toLowerCase()}`}><i />{titleCase(selectedMember.status)}</span></div>
            </div>
            <div className="u-drawer-actions">
              <Button size="sm" color="secondary" iconLeading={Edit03} onPress={() => navigate(`/members/${selectedMember.id}/edit`)}>Edit</Button>
              <Button size="sm" color="secondary" iconLeading={Ruler} onPress={() => navigate(`/members/${selectedMember.id}/measurements`)}>Measurements</Button>
            </div>
            <div className="u-drawer-tabs"><button className="active" type="button">Overview</button><button type="button" onClick={() => navigate(`/workout-plan/${selectedMember.id}`)}>Workouts</button></div>
            <div className="u-detail-section">
              <h4>Membership</h4>
              <dl><div><dt>Plan</dt><dd>{titleCase(selectedMember.membershipType)}</dd></div><div><dt>Start date</dt><dd>{formatDate(selectedMember.startDate)}</dd></div><div><dt>Status</dt><dd>{titleCase(selectedMember.status)}</dd></div></dl>
            </div>
            <div className="u-detail-section">
              <h4>Contact</h4>
              <dl><div><dt>Email</dt><dd>{selectedMember.email || '—'}</dd></div><div><dt>Phone</dt><dd>{selectedMember.phone || '—'}</dd></div><div><dt>NIC / Passport</dt><dd>{selectedMember.nicPassport || '—'}</dd></div></dl>
              <div className="u-contact-actions">
                <Button size="sm" color="secondary" iconLeading={Phone01} onPress={() => selectedMember.phone && window.open(`tel:${selectedMember.phone}`, '_self')}>Call</Button>
                <Button size="sm" color="secondary" iconLeading={Mail01} onPress={() => selectedMember.email && window.open(`mailto:${selectedMember.email}`, '_self')}>Email</Button>
              </div>
            </div>
            <div className="u-drawer-footer">
              <Button size="md" color={selectedMember.status.toUpperCase() === 'ACTIVE' ? 'secondary-destructive' : 'primary'} onPress={() => updateStatus(selectedMember)}>{selectedMember.status.toUpperCase() === 'ACTIVE' ? 'Suspend member' : 'Activate member'}</Button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default Members;
